import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WorkshopSessionList.css';

function WorkshopSessionList() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchSessions();
  }, [filterLocation, filterStatus, dateFrom, dateTo]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterLocation) params.location = filterLocation;
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.startDate = dateFrom;
      if (dateTo) params.endDate = dateTo;

      const response = await axios.get('/api/workshops/sessions', { params });
      setSessions(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const getCapacityPercent = (booked, capacity) => {
    if (!capacity) return 0;
    return Math.min(100, Math.round((booked / capacity) * 100));
  };

  const getStatusClass = (status) => {
    const map = {
      scheduled: 'blue',
      full: 'orange',
      cancelled: 'red',
      completed: 'gray'
    };
    return map[status] || 'gray';
  };

  if (loading && sessions.length === 0) {
    return (
      <div className="workshop-session-list">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workshop-session-list">
      <div className="ws-page-header">
        <h1>Workshop Sessions</h1>
        <div className="ws-header-actions">
          <button className="ws-btn-secondary" onClick={() => navigate('/workshops/calendar')}>
            Calendar View
          </button>
          <button className="ws-btn-primary" onClick={() => navigate('/workshops/sessions/new')}>
            New Session
          </button>
        </div>
      </div>

      <div className="ws-filters">
        <div className="ws-filter-group">
          <label>Location</label>
          <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}>
            <option value="">All Locations</option>
            <option value="charleston">Charleston</option>
            <option value="boston">Boston</option>
            <option value="atlanta">Atlanta</option>
          </select>
        </div>
        <div className="ws-filter-group">
          <label>Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="full">Full</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="ws-filter-group">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="ws-filter-group">
          <label>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        {(filterLocation || filterStatus || dateFrom || dateTo) && (
          <button
            className="ws-btn-secondary ws-btn-sm"
            onClick={() => {
              setFilterLocation('');
              setFilterStatus('');
              setDateFrom('');
              setDateTo('');
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {error && <div className="ws-error">{error}</div>}

      <div className="ws-section">
        {sessions.length === 0 ? (
          <div className="ws-empty">No sessions found matching the selected filters</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => {
                  const percent = getCapacityPercent(session.booked_count || 0, session.capacity);
                  return (
                    <tr
                      key={session.id}
                      className="ws-clickable-row"
                      onClick={() => navigate(`/workshops/sessions/${session.id}`)}
                    >
                      <td className="ws-text-bold">{formatDate(session.session_date)}</td>
                      <td>{formatTime(session.start_time)} - {formatTime(session.end_time)}</td>
                      <td>{session.location_name || session.location_slug}</td>
                      <td>{session.workshop_type_name || 'Workshop'}</td>
                      <td>
                        <div className="ws-capacity-cell">
                          <span className="ws-capacity-text">
                            {session.booked_count || 0}/{session.capacity}
                          </span>
                          <div className="ws-capacity-bar">
                            <div
                              className="ws-capacity-fill"
                              style={{
                                width: `${percent}%`,
                                backgroundColor: percent >= 100 ? '#e65100' : '#8B6F4E'
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`ws-status-badge ws-status-${getStatusClass(session.status)}`}>
                          {session.status}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="ws-btn-secondary ws-btn-sm"
                          onClick={() => navigate(`/workshops/sessions/${session.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkshopSessionList;
